import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { Right, Role } from '@taxi/shared';

/**
 * Routes du back-office.
 *
 * Toutes les sections exigent `BO_ACCESS`, plus le droit propre à la section.
 * Les droits d'une route parente s'ajoutent à ceux de ses enfants : la garde et
 * le menu appliquent donc la même règle sans redéclaration.
 */
const routes: RouteRecordRaw[] = [
	{
		path: '/connexion',
		name: 'login',
		component: () => import('@/views/LoginView.vue'),
		meta: { publicPage: true },
	},
	{
		path: '/',
		name: 'home',
		component: () => import('@/views/DashboardView.vue'),
		meta: { granted: [Right.BO_ACCESS], title: 'Tableau de bord' },
	},
	{
		path: '/carte',
		name: 'live-map',
		component: () => import('@/views/LiveMapView.vue'),
		meta: { granted: [Right.BO_LIVE_MAP], title: 'Carte en direct' },
	},
	{
		path: '/courses',
		name: 'rides',
		component: () => import('@/views/RideListView.vue'),
		meta: { granted: [Right.RIDE_READ_ALL], title: 'Courses' },
	},
	{
		// Le fil d'exploitation : ce qui se passe maintenant, toutes courses
		// confondues. Complète le registre, qui répond au passé.
		path: '/direct',
		name: 'live-timeline',
		component: () => import('@/views/LiveTimelineView.vue'),
		meta: { granted: [Right.RIDE_READ_ALL], title: 'En direct' },
	},
	{
		path: '/courses/:id',
		name: 'ride-detail',
		component: () => import('@/views/RideDetailView.vue'),
		meta: { granted: [Right.RIDE_READ_ALL], title: 'Détail de la course' },
	},
	{
		// Un chauffeur est un compte qui porte le rôle correspondant : c'est le
		// même annuaire, filtré, avec les colonnes du véhicule en plus. Deux écrans
		// distincts finiraient par diverger sur ce qu'ils savent d'une même personne.
		path: '/chauffeurs',
		name: 'drivers',
		component: () => import('@/views/UserListView.vue'),
		meta: {
			granted: [Right.USER_READ_ALL, Right.DRIVER_READ_ALL],
			title: 'Chauffeurs',
			role: Role.DRIVER,
		},
	},
	{
		path: '/bornes',
		name: 'kiosks',
		component: () => import('@/views/KioskListView.vue'),
		meta: { granted: [Right.KIOSK_READ_ALL], title: 'Bornes' },
	},
	{
		path: '/bornes/:id',
		name: 'kiosk-detail',
		component: () => import('@/views/KioskDetailView.vue'),
		meta: { granted: [Right.KIOSK_READ_ALL], title: 'Détail de la borne' },
	},
	{
		path: '/lieux',
		name: 'places',
		component: () => import('@/views/PlaceListView.vue'),
		meta: { granted: [Right.PLACE_READ], title: 'Lieux' },
	},
	{
		path: '/utilisateurs',
		name: 'users',
		component: () => import('@/views/UserListView.vue'),
		meta: { granted: [Right.USER_READ_ALL], title: 'Utilisateurs' },
	},
	{
		path: '/parametres',
		name: 'settings',
		component: () => import('@/views/SettingsView.vue'),
		meta: { granted: [Right.SETTINGS_READ], title: 'Paramètres' },
	},
	{
		path: '/droits',
		name: 'rights',
		component: () => import('@/views/RightsView.vue'),
		meta: { granted: [Right.RIGHTS_READ], title: 'Rôles et droits' },
	},
	{
		path: '/:catchAll(.*)',
		redirect: { name: 'home' },
	},
];

const router = createRouter({
	history: createWebHistory(),
	routes,
	scrollBehavior: () => ({ top: 0 }),
});

export default router;
